import React, { useState } from 'react';
import { 
  Globe, 
  ExternalLink, 
  Clock, 
  Filter, 
  Search, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  BookOpen,
  Zap,
  Tag,
  Newspaper
} from 'lucide-react';
import { cn } from '../utils';
import { NewsItem } from '../types';
import { toast } from 'sonner';

const News: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSentiment, setSelectedSentiment] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [error, setError] = useState<string | null>(null);
  
  // Mock news data - sempre disponibile
  const [news] = useState<NewsItem[]>([
    {
      id: 'mock-1',
      title: 'Market Update: Stocks Show Mixed Performance',
      summary: 'Major indices display varied performance as investors digest latest economic data and corporate earnings reports.',
      url: 'https://finance.yahoo.com/news/market-update-stocks-mixed-performance',
      publishedAt: new Date().toISOString(),
      source: 'Market Alert',
      category: 'markets',
      sentiment: 'neutral',
      affectedAssets: ['SPY', 'QQQ']
    },
    {
      id: 'mock-2',
      title: 'Federal Reserve Maintains Current Interest Rates',
      summary: 'The Fed keeps rates unchanged while signaling potential adjustments based on economic indicators.',
      url: 'https://www.ft.com/content/federal-reserve-interest-rates',
      publishedAt: new Date(Date.now() - 3600000).toISOString(),
      source: 'Financial Times',
      category: 'economics',
      sentiment: 'neutral',
      affectedAssets: ['USD', 'BONDS']
    },
    {
      id: 'mock-3',
      title: 'Tech Sector Rallies on AI Developments',
      summary: 'Technology stocks surge following breakthrough AI announcements from major companies.',
      url: 'https://techcrunch.com/tech-sector-ai-developments',
      publishedAt: new Date(Date.now() - 7200000).toISOString(),
      source: 'TechCrunch',
      category: 'technology',
      sentiment: 'positive',
      affectedAssets: ['NVDA', 'MSFT', 'GOOGL']
    },
    {
      id: 'mock-4',
      title: 'Oil Prices Stabilize After Recent Volatility',
      summary: 'Crude oil futures find support as supply concerns balance with demand forecasts.',
      url: 'https://www.reuters.com/business/energy/oil-prices-stabilize',
      publishedAt: new Date(Date.now() - 10800000).toISOString(),
      source: 'Energy News',
      category: 'commodities',
      sentiment: 'neutral',
      affectedAssets: ['OIL', 'XLE']
    },
    {
      id: 'mock-5',
      title: 'Cryptocurrency Market Shows Resilience',
      summary: 'Digital assets maintain stability despite ongoing regulatory discussions worldwide.',
      url: 'https://www.coindesk.com/markets/cryptocurrency-resilience',
      publishedAt: new Date(Date.now() - 14400000).toISOString(),
      source: 'Crypto Daily',
      category: 'crypto',
      sentiment: 'positive',
      affectedAssets: ['BTC', 'ETH']
    },
    {
      id: 'mock-6',
      title: 'European Markets Close Higher on Strong Earnings',
      summary: 'European indices end positive following better-than-expected earnings from major corporations.',
      url: 'https://www.reuters.com/markets/europe/european-markets-earnings',
      publishedAt: new Date(Date.now() - 18000000).toISOString(),
      source: 'Reuters',
      category: 'markets',
      sentiment: 'positive',
      affectedAssets: ['EUROSTOXX', 'EUR']
    }
  ]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setLastUpdate(new Date());
    toast.success('News aggiornate!');
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const categories: Record<string, string> = {
    all: 'All News',
    breaking: 'Breaking News',
    markets: 'Markets',
    crypto: 'Cryptocurrency',
    forex: 'Forex',
    commodities: 'Commodities',
    economics: 'Economics',
    technology: 'Technology',
    earnings: 'Earnings',
    analysis: 'Analysis'
  };

  const filteredNews = news.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.affectedAssets?.some(asset => asset.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSentiment = selectedSentiment === 'all' || item.sentiment === selectedSentiment;
    return matchesSearch && matchesCategory && matchesSentiment;
  });



  const handleNewsClick = (url: string) => {
    if (url && url !== '#' && url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'negative':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-400 bg-green-500/20';
      case 'negative': return 'text-red-400 bg-red-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'breaking': return <Zap className="w-4 h-4" />;
      case 'markets': return <TrendingUp className="w-4 h-4" />;
      case 'crypto': return <AlertCircle className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'breaking': return 'text-red-400 bg-red-500/20';
      case 'markets': return 'text-blue-400 bg-blue-500/20';
      case 'crypto': return 'text-purple-400 bg-purple-500/20';
      case 'economics': return 'text-green-400 bg-green-500/20';
      case 'technology': return 'text-cyan-400 bg-cyan-500/20';
      case 'earnings': return 'text-orange-400 bg-orange-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Financial News</h1>
          <p className="text-gray-400">Latest market news, analysis, and breaking financial updates</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={cn(
              "flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all duration-200",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
            Refresh
          </button>
          
          <div className="text-sm text-gray-400">
            Last update: {lastUpdate.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* News Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-400">Total Articles</span>
          </div>
          <p className="text-xl font-bold text-white">{news.length}</p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-red-400" />
            <span className="text-sm text-gray-400">Breaking News</span>
          </div>
          <p className="text-xl font-bold text-red-400">
            {news.filter(item => item.category === 'breaking').length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-sm text-gray-400">Positive</span>
          </div>
          <p className="text-xl font-bold text-green-400">
            {news.filter(item => item.sentiment === 'positive').length}
          </p>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-gray-400">Last Hour</span>
          </div>
          <p className="text-xl font-bold text-orange-400">
            {news.filter(item => {
              const newsTime = new Date(item.publishedAt).getTime();
              const oneHourAgo = Date.now() - (60 * 60 * 1000);
              return newsTime > oneHourAgo;
            }).length}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="glass-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search news, assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Sentiment Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={selectedSentiment}
              onChange={(e) => setSelectedSentiment(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all" className="bg-slate-800">All Sentiments</option>
              <option value="positive" className="bg-slate-800">Positive</option>
              <option value="neutral" className="bg-slate-800">Neutral</option>
              <option value="negative" className="bg-slate-800">Negative</option>
            </select>
          </div>
          
          {/* Category Filter */}
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              {Object.entries(categories).map(([key, label]) => (
                <option key={key} value={key} className="bg-slate-800">{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* News Grid */}
      <div className="grid gap-6">
        {error ? (
          <div className="glass-card p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 text-lg mb-4">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
            >
              Dismiss
            </button>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Newspaper className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No news found matching your criteria</p>
          </div>
        ) : (
          filteredNews.map((newsItem) => {
            const timeAgo = Math.floor((Date.now() - new Date(newsItem.publishedAt).getTime()) / (1000 * 60));
            
            return (
              <div 
                key={newsItem.id} 
                onClick={() => handleNewsClick(newsItem.url)}
                className="glass-card p-6 hover:bg-white/5 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getSentimentIcon(newsItem.sentiment || 'neutral')}
                    <div className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded text-xs font-medium",
                      getCategoryColor(newsItem.category)
                    )}>
                      {getCategoryIcon(newsItem.category)}
                      {newsItem.category}
                    </div>
                    
                    <div className={cn(
                      "px-2 py-1 rounded text-xs font-medium",
                      getSentimentColor(newsItem.sentiment || 'neutral')
                    )}>
                      {(newsItem.sentiment || 'neutral').toUpperCase()}
                    </div>
                  </div>
                  
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors line-clamp-2">
                  {newsItem.title}
                </h3>
                
                <p className="text-gray-300 mb-4 line-clamp-3">
                  {newsItem.summary}
                </p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className="text-blue-400 font-medium">{newsItem.source}</span>
                    <div className="flex items-center gap-1 text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>{timeAgo < 60 ? `${timeAgo}m` : `${Math.floor(timeAgo / 60)}h`} ago</span>
                    </div>
                  </div>
                  
                  {newsItem.affectedAssets && newsItem.affectedAssets.length > 0 && (
                    <div className="flex items-center gap-2">
                      {newsItem.affectedAssets.slice(0, 3).map((asset, index) => (
                        <span key={index} className="px-2 py-1 bg-white/5 text-gray-400 rounded text-xs">
                          {asset}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Stats Footer */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Showing {filteredNews.length} of {news.length} articles</span>
          <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  );
};

export default News;